/**
 * Per-Job Profitability — Wave E.
 *
 * Pure derivation over the posted GL. Every line we post already carries a
 * `jobId` (when relevant) plus a tagged memo bucket — so the report has zero
 * new data, just a new lens.
 *
 * Buckets (subtype-driven, not memo-driven, so trade-account renames don't break):
 *   • revenue          → credits on subtype === "revenue"
 *   • costMaterial     → debits  on subtype === "cost_of_material"
 *   • costLabor        → debits  on subtype === "cost_of_labor"
 *   • wipBalance       → net debit on subtype === "wip"           (cost capitalized, not yet released)
 *   • deferredRevenue  → net credit on subtype === "deferred_revenue" (billed, not yet earned)
 *
 * Margin definitions:
 *   • grossMargin   = revenue - (costMaterial + costLabor)
 *   • grossMarginPct = grossMargin / revenue (or null when revenue == 0)
 *   • forecastMargin = grossMargin - wipBalance   (what gross margin becomes
 *     once outstanding WIP releases at zero additional billing; pessimistic).
 *
 * Status:
 *   • "in_progress"  — has WIP balance or deferred revenue still outstanding
 *   • "completed"    — no WIP, no deferred revenue, has revenue
 *   • "no_activity"  — nothing posted (placeholder; filtered out by default)
 */
import type { JournalEntry, GLAccount } from "../glTypes";

export interface JobProfitabilityRow {
  jobId: string;
  customerName?: string;
  status: "in_progress" | "completed" | "no_activity";
  revenue: number;
  costMaterial: number;
  costLabor: number;
  totalCost: number;
  grossMargin: number;
  grossMarginPct: number | null;
  wipBalance: number;
  deferredRevenue: number;
  /** Pessimistic margin if all WIP releases without further billing. */
  forecastMargin: number;
  /** Distinct posted journal entries touching this job. */
  entryCount: number;
  /** ISO of the most recent posting against this job. */
  lastActivityAt?: string;
}

export interface JobProfitabilitySummary {
  rows: JobProfitabilityRow[];
  totals: Omit<JobProfitabilityRow, "jobId" | "customerName" | "status" | "lastActivityAt" | "grossMarginPct"> & {
    grossMarginPct: number | null;
    jobCount: number;
  };
}

const round2 = (n: number) => Math.round(n * 100) / 100;

export interface JobProfitabilityInput {
  journalEntries: JournalEntry[];
  accounts: GLAccount[];
  /** Optional id→customer-name lookup for friendlier display. */
  customerNameByJobId?: Record<string, string>;
}

export function getJobProfitability(input: JobProfitabilityInput): JobProfitabilitySummary {
  const acctById = new Map(input.accounts.map(a => [a.id, a]));
  const bucket = new Map<string, JobProfitabilityRow>();

  const ensure = (jobId: string): JobProfitabilityRow => {
    let row = bucket.get(jobId);
    if (!row) {
      row = {
        jobId,
        customerName: input.customerNameByJobId?.[jobId],
        status: "no_activity",
        revenue: 0, costMaterial: 0, costLabor: 0, totalCost: 0,
        grossMargin: 0, grossMarginPct: null,
        wipBalance: 0, deferredRevenue: 0, forecastMargin: 0,
        entryCount: 0,
      };
      bucket.set(jobId, row);
    }
    return row;
  };

  const touchedBy = new Map<string, Set<string>>(); // jobId → JE ids

  for (const je of input.journalEntries) {
    if (je.status !== "posted") continue;
    for (const l of je.lines) {
      if (!l.jobId) continue;
      const acc = acctById.get(l.accountId);
      if (!acc) continue;
      const row = ensure(l.jobId);

      // Track distinct JEs per job
      let set = touchedBy.get(l.jobId);
      if (!set) { set = new Set(); touchedBy.set(l.jobId, set); }
      set.add(je.id);

      // Last activity
      if (!row.lastActivityAt || je.postingDate > row.lastActivityAt) {
        row.lastActivityAt = je.postingDate;
      }

      switch (acc.subtype) {
        case "revenue":
          row.revenue += l.side === "credit" ? l.amount : -l.amount;
          break;
        case "cost_of_material":
          row.costMaterial += l.side === "debit" ? l.amount : -l.amount;
          break;
        case "cost_of_labor":
          row.costLabor += l.side === "debit" ? l.amount : -l.amount;
          break;
        case "wip":
          row.wipBalance += l.side === "debit" ? l.amount : -l.amount;
          break;
        case "deferred_revenue":
          row.deferredRevenue += l.side === "credit" ? l.amount : -l.amount;
          break;
        default:
          break;
      }
    }
  }

  const rows: JobProfitabilityRow[] = [];
  for (const row of bucket.values()) {
    row.revenue = round2(row.revenue);
    row.costMaterial = round2(Math.max(0, row.costMaterial));
    row.costLabor = round2(Math.max(0, row.costLabor));
    row.totalCost = round2(row.costMaterial + row.costLabor);
    row.grossMargin = round2(row.revenue - row.totalCost);
    row.grossMarginPct = row.revenue > 0 ? round2(row.grossMargin / row.revenue) : null;
    row.wipBalance = round2(Math.max(0, row.wipBalance));
    row.deferredRevenue = round2(Math.max(0, row.deferredRevenue));
    row.forecastMargin = round2(row.grossMargin - row.wipBalance);
    row.entryCount = touchedBy.get(row.jobId)?.size ?? 0;

    if (row.wipBalance > 0.005 || row.deferredRevenue > 0.005) row.status = "in_progress";
    else if (row.revenue > 0.005) row.status = "completed";
    else row.status = "no_activity";

    rows.push(row);
  }

  // Sort: in_progress first (active money), then by revenue desc.
  rows.sort((a, b) => {
    if (a.status !== b.status) {
      const order = { in_progress: 0, completed: 1, no_activity: 2 };
      return order[a.status] - order[b.status];
    }
    return b.revenue - a.revenue;
  });

  // Totals (skip "no_activity" — they'd dilute the average)
  const live = rows.filter(r => r.status !== "no_activity");
  const sum = (k: keyof JobProfitabilityRow) =>
    round2(live.reduce((s, r) => s + (typeof r[k] === "number" ? (r[k] as number) : 0), 0));
  const totalRevenue = sum("revenue");
  const totalMaterial = sum("costMaterial");
  const totalLabor = sum("costLabor");
  const totalCost = round2(totalMaterial + totalLabor);
  const totalMargin = round2(totalRevenue - totalCost);
  const totalWip = sum("wipBalance");
  const totalDeferred = sum("deferredRevenue");

  return {
    rows,
    totals: {
      jobCount: live.length,
      revenue: totalRevenue,
      costMaterial: totalMaterial,
      costLabor: totalLabor,
      totalCost,
      grossMargin: totalMargin,
      grossMarginPct: totalRevenue > 0 ? round2(totalMargin / totalRevenue) : null,
      wipBalance: totalWip,
      deferredRevenue: totalDeferred,
      forecastMargin: round2(totalMargin - totalWip),
      entryCount: live.reduce((s, r) => s + r.entryCount, 0),
    },
  };
}
